import React, { FC, Fragment, PropsWithChildren, useCallback, useRef } from "react";
import { ScreenProjection } from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Modal } from "@lookiero/sty-psp-ui";
import { useTrackScreenNavigationBack } from "../../../tracking/useTrackScreenNavigationBack";
import { HostScreenOnSuccessFunction } from "../../components/organisms/question/components/hostScreenCta/HostScreenCta";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { OnScreenLoadedFunction, Screen } from "./Screen";

interface ScreenModalProps {
  readonly providers?: FC<PropsWithChildren>;
  readonly slug?: string;
  readonly onBack: () => void;
  readonly onSuccess: HostScreenOnSuccessFunction;
  readonly onScreenNotFound?: () => void;
}

const ScreenModal: FC<ScreenModalProps> = ({
  providers: Providers = Fragment,
  slug,
  onBack,
  onSuccess,
  onScreenNotFound,
}) => {
  const modalVisible = Boolean(slug);
  const scrollRef = useRef(null);

  const {
    customer: { customerId, country, segment },
    tradename,
  } = useStaticInfo();
  const trackScreenNavigationBack = useTrackScreenNavigationBack({
    country,
    segment,
    customerId,
    tradename,
  });

  const screenRef = useRef<ScreenProjection>(null);
  const handleOnScreenLoaded: OnScreenLoadedFunction = useCallback(({ screen }) => {
    screenRef.current = screen;
  }, []);

  const handleOnBack = useCallback(() => {
    trackScreenNavigationBack({
      slug: screenRef.current?.metadata.slug,
      group: screenRef.current?.metadata.groupTranslationKey,
    });

    if (onBack) {
      onBack();
    }
  }, [onBack, trackScreenNavigationBack]);

  return (
    <Modal
      portalHostName="StyleProfile"
      scrollRef={scrollRef}
      testID="screen"
      visible={modalVisible}
      scroll
      showCloseButton
      onClose={handleOnBack}
    >
      <Providers>
        <Screen
          scrollRef={scrollRef}
          slug={slug}
          onScreenLoaded={handleOnScreenLoaded}
          onScreenNotFound={onScreenNotFound}
          onSuccess={onSuccess}
        />
      </Providers>
    </Modal>
  );
};

export { ScreenModal };
